<template>
  <div
    class="custom-split-resizer"
    :class="{
      'is-active': isDragging,
      'is-hovered': isHovered,
      'is-collapsed': isFullyExpanded
    }"
    role="separator"
    tabindex="0"
    :aria-valuenow="Math.round(leftWidth)"
    :aria-valuemin="minWidth"
    :aria-valuemax="maxWidth"
    aria-orientation="vertical"
    @mousedown.prevent="onMouseDown"
    @touchstart.prevent="onTouchStart"
    @dblclick="onDoubleClick"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div
      class="resizer-top-indicator"
      :class="{ 'is-modified': isModified, 'is-maximized': isFullyExpanded }"
      :title="topIndicatorTitle"
      @click.stop="onTopIndicatorClick"
    >
      <div class="top-pill">
        <template v-if="isFullyExpanded">
          <i class="fa fa-columns mr-1" style="font-size: 10px;"></i>
          <span>Abrir despachos</span>
        </template>
        <template v-else-if="isModified">
          <i class="fa fa-undo mr-1" style="font-size: 9px;"></i>
          <span>2x clique restaura</span>
        </template>
        <template v-else>
          <i class="fa fa-arrows-h mr-1" style="font-size: 9px;"></i>
          <span>Arrastar</span>
        </template>
      </div>
    </div>

    <div class="resizer-line"></div>

    <div class="resizer-center-wrapper">
      <div
        class="resizer-handle"
        :class="{ 'is-collapsed-handle': isFullyExpanded }"
        :title="isFullyExpanded ? 'Clique para reabrir despachos (70%)' : 'Arraste ou clique na seta para expandir 100%'"
      >
        <i v-if="isFullyExpanded" class="fa fa-chevron-left handle-icon"></i>
        <i v-else class="fa fa-arrows-h handle-icon"></i>

        <button
          v-if="!isFullyExpanded"
          type="button"
          class="handle-collapse-btn"
          title="Expandir documento para 100%"
          @click.stop="$emit('toggle-collapse')"
        >
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>

      <transition name="fade-slide">
        <div
          v-if="isDragging"
          class="side-floating-badge is-dragging-badge"
          :class="{ 'badge-flip-left': isFullyExpanded || leftWidth > 80 }"
        >
          <div class="badge-content">
            <span class="badge-value">{{ Math.round(leftWidth) }}%</span>
            <span class="badge-label">{{ isFullyExpanded ? 'documento 100% (tela cheia)' : 'largura do documento' }}</span>
          </div>
        </div>

        <div
          v-else-if="isHovered"
          class="side-floating-badge is-hover-badge"
          :class="{ 'badge-flip-left': isFullyExpanded || leftWidth > 80 }"
        >
          <div class="badge-content">
            <template v-if="isFullyExpanded">
              <span class="badge-hint-title"><i class="fa fa-columns mr-1"></i> Reabrir despachos</span>
              <span class="badge-hint-sub">Retorna para 70% padrão</span>
            </template>
            <template v-else-if="isModified">
              <span class="badge-hint-title"><i class="fa fa-undo mr-1"></i> Duplo clique restaura</span>
              <span class="badge-hint-sub">Retorna para 70% padrão</span>
            </template>
            <template v-else>
              <span class="badge-hint-title"><i class="fa fa-arrows-h mr-1"></i> Arraste para ajustar</span>
              <span class="badge-hint-sub">Arraste até o fim para 100%</span>
            </template>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SplitResizer',
  props: {
    isDragging: {
      type: Boolean,
      default: false
    },
    leftWidth: {
      type: Number,
      default: 70
    },
    defaultWidth: {
      type: Number,
      default: 70
    },
    minWidth: {
      type: Number,
      default: 35
    },
    maxWidth: {
      type: Number,
      default: 100
    },
    isCollapsed: {
      type: Boolean,
      default: false
    }
  },
  emits: ['start-drag', 'reset', 'toggle-collapse'],
  data () {
    return {
      isHovered: false
    };
  },
  computed: {
    isFullyExpanded () {
      return this.isCollapsed || this.leftWidth >= 96;
    },
    isModified () {
      return Math.abs(this.leftWidth - this.defaultWidth) >= 1;
    },
    topIndicatorTitle () {
      if (this.isFullyExpanded) {
        return 'Clique para reabrir o painel de despachos (70%)';
      }
      if (this.isModified) {
        return 'Clique para restaurar tamanho padrão (70%)';
      }
      return 'Arraste para redimensionar';
    }
  },
  methods: {
    onMouseDown (e) {
      this.$emit('start-drag', e);
    },
    onTouchStart (e) {
      if (e.touches && e.touches[0]) {
        this.$emit('start-drag', e.touches[0]);
      }
    },
    onDoubleClick () {
      this.$emit('reset');
    },
    onTopIndicatorClick () {
      if (this.isFullyExpanded) {
        this.$emit('toggle-collapse');
      } else if (this.isModified) {
        this.$emit('reset');
      }
    }
  }
};
</script>

<style scoped>
.custom-split-resizer {
  width: 32px;
  position: relative;
  cursor: col-resize;
  display: flex;
  flex-direction: column;
  align-items: center;
  user-select: none;
  touch-action: none;
  z-index: 25;
  flex-shrink: 0;
  margin: 0;
  outline: none;
  transition: all 0.2s ease;
}

.custom-split-resizer.is-collapsed {
  cursor: pointer;
}

.resizer-top-indicator {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  cursor: pointer;
  transition: all 0.25s ease;
}

.custom-split-resizer.is-collapsed .resizer-top-indicator,
.resizer-top-indicator.is-maximized {
  left: auto;
  right: 0px;
  transform: none;
}

.top-pill {
  display: flex;
  align-items: center;
  background: #FFF7ED;
  color: #EA580C;
  border: 1px solid #FDBA74;
  border-radius: 12px;
  padding: 3px 8px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(241, 134, 11, 0.18);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.resizer-top-indicator.is-modified .top-pill {
  background: #EFF6FF;
  color: #2563EB;
  border-color: #93C5FD;
  box-shadow: 0 1px 4px rgba(37, 99, 235, 0.18);
}

.resizer-top-indicator.is-modified:hover .top-pill {
  background: #2563EB;
  color: #FFFFFF;
  border-color: #2563EB;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.4);
  transform: scale(1.06);
}

.resizer-top-indicator.is-maximized .top-pill {
  background: #059669;
  color: #FFFFFF;
  border: 1px solid #047857;
  padding: 4px 10px;
  font-size: 10px;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.35);
}

.resizer-top-indicator.is-maximized:hover .top-pill {
  background: #047857;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.5);
  transform: scale(1.05);
}

.custom-split-resizer:hover .top-pill:not(.is-modified):not(.is-maximized),
.custom-split-resizer.is-active .top-pill:not(.is-modified):not(.is-maximized) {
  background: #F1860B;
  color: #FFFFFF;
  border-color: #F1860B;
  box-shadow: 0 2px 6px rgba(241, 134, 11, 0.4);
  transform: scale(1.06);
}

.resizer-line {
  position: absolute;
  top: 14px;
  bottom: 0;
  left: 50%;
  width: 3px;
  transform: translateX(-50%);
  background: rgba(241, 134, 11, 0.4);
  border-radius: 2px;
  transition: all 0.25s ease;
}

.custom-split-resizer:hover .resizer-line,
.custom-split-resizer.is-active .resizer-line {
  background: #F1860B;
  width: 4px;
  box-shadow: 0 0 10px rgba(241, 134, 11, 0.55);
}

.resizer-center-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 6;
  transition: all 0.2s ease;
}

.resizer-handle {
  width: 26px;
  height: 52px;
  background: #FFFFFF;
  border: 2px solid rgba(241, 134, 11, 0.5);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.resizer-handle.is-collapsed-handle {
  border-color: #059669;
  background: #FFFFFF;
  box-shadow: 0 2px 10px rgba(5, 150, 105, 0.25);
}

.resizer-handle.is-collapsed-handle .handle-icon {
  color: #059669;
  font-size: 13px;
}

.handle-icon {
  font-size: 11px;
  color: #F1860B;
  transition: transform 0.2s ease;
}

.handle-collapse-btn {
  background: transparent;
  border: none;
  padding: 0;
  margin-top: 2px;
  color: #F1860B;
  font-size: 9px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.handle-collapse-btn:hover {
  opacity: 1;
  transform: scale(1.2);
}

.custom-split-resizer:hover .resizer-handle:not(.is-collapsed-handle),
.custom-split-resizer.is-active .resizer-handle:not(.is-collapsed-handle) {
  background: #F1860B;
  border-color: #F1860B;
  box-shadow: 0 4px 16px rgba(241, 134, 11, 0.45);
  transform: scale(1.1);
}

.custom-split-resizer:hover .resizer-handle.is-collapsed-handle,
.custom-split-resizer.is-active .resizer-handle.is-collapsed-handle {
  background: #059669;
  border-color: #059669;
  box-shadow: 0 4px 16px rgba(5, 150, 105, 0.45);
  transform: scale(1.1);
}

.custom-split-resizer:hover .handle-icon,
.custom-split-resizer.is-active .handle-icon,
.custom-split-resizer:hover .handle-collapse-btn,
.custom-split-resizer.is-active .handle-collapse-btn {
  color: #FFFFFF;
}

.side-floating-badge {
  position: absolute;
  left: 36px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  white-space: nowrap;
  z-index: 30;
  transition: all 0.2s ease;
}

.side-floating-badge.badge-flip-left {
  left: auto;
  right: 36px;
}

.badge-content {
  background: #1E293B;
  color: #FFFFFF;
  padding: 6px 12px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.3);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: 1px solid rgba(255, 255, 255, 0.12);
  position: relative;
}

.badge-content::before {
  content: '';
  position: absolute;
  top: 50%;
  left: -5px;
  transform: translateY(-50%);
  border-width: 5px 5px 5px 0;
  border-style: solid;
  border-color: transparent #1E293B transparent transparent;
}

.badge-flip-left .badge-content::before {
  left: auto;
  right: -5px;
  border-width: 5px 0 5px 5px;
  border-color: transparent transparent transparent #1E293B;
}

.is-dragging-badge .badge-content {
  background: #0F172A;
  border-color: #F1860B;
  box-shadow: 0 6px 20px rgba(241, 134, 11, 0.35);
}

.is-dragging-badge .badge-content::before {
  border-color: transparent #F1860B transparent transparent;
}

.is-dragging-badge.badge-flip-left .badge-content::before {
  border-color: transparent transparent transparent #F1860B;
}

.badge-value {
  font-size: 15px;
  font-weight: 800;
  color: #FBBF24;
  line-height: 1.1;
}

.badge-label {
  font-size: 10px;
  color: #94A3B8;
  font-weight: 500;
  margin-top: 2px;
}

.badge-hint-title {
  font-size: 11px;
  font-weight: 700;
  color: #F8FAFC;
}

.badge-hint-sub {
  font-size: 9.5px;
  color: #94A3B8;
  margin-top: 1px;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-50%) translateX(-6px);
}
</style>
